<!-- eslint-disable max-len -->
<template lang="pug">
div.stickers-list-container
    div.sticker-card(v-for="item in stickers" @click="gotoStickerDetails(item.id)")
      // Sticker Image Display
      .sticker-image-wrapper
        .img-uploader.pos-r.list-image-container
          template(v-if="item.url")
            img(:src="item.url" @error="(e) => handleImageError(e)").img-fluid.list-image-item

            // Campaign Badge (if not regular sticker)
            .campaign-badge(
              v-if="item.campaignType && item.campaignType !== 'Sticker'"
              :class="getCampaignBadgeClass(item.campaignType)"
            )
              span {{ item.campaignType.toUpperCase() }}

      // Sticker Info Display (Creator + Title)
      .sticker-info-section
        // Creator info
        .creator-info.mb-1
          .row.row-xs.align-items-center
            .col-auto
              img(
                :src="(item.entity==='User' && item.user && item.user.avatar) || (item.entity==='Organization' && item.organization && item.organization.avatar) ? (item.entity==='User' ? item.user.avatar : item.organization.avatar) : getPlaceholderForEntity(item)"
                @error="(e) => handleImageError(e, item)"
              ).d-block.size-24.obj-f-c.rounded-circle
            .col.lh-1
              span(v-if="item.entity==='User' && item.user").sticker-author {{ item.user.firstname }} {{ item.user.lastname }}
              span(v-else-if="item.entity==='Organization' && item.organization").sticker-author {{ item.organization.name }}
              span(v-else).sticker-author Spotlight

        // Sticker title
        .sticker-title
          h6.mb-0 {{ item.title }}

      // Sticker Pricing
      .sticker-pricing
        span.price-text
          // Show purchase price if available, otherwise show sticker cost
          span(v-if="showPurchaseDetails && item.purchasePrice !== undefined")
            span(v-if="item.purchasePrice <= 0") Free
            span(v-else) ${{ item.purchasePrice.toFixed(2) }}
          span(v-else-if="item.campaignType === 'Ticket' && item.priceRange") {{ item.priceRange }}
          span(v-else-if="item.campaignType === 'Sweepstakes' && item.sweepstakesSticker?.entryCost?.length > 0")
            | $0-${{ Math.max(...item.sweepstakesSticker.entryCost) }}
          span(v-else-if="Array.isArray(item?.quickCosts) && item.quickCosts.length > 0")
            | ${{ Math.min(...item.quickCosts) + ' - ' + '$' + Math.max(...item.quickCosts) }}
          span(v-else-if="item.cost > 0")
            | ${{ item.cost.toFixed(2) }}
          span(v-else) Free

      // Purchase date for purchase history
      div(v-if="showPurchaseDetails && item.purchaseDate")
        .fs-12.text-muted.mt-1
          span Purchased: {{ formatDate(item.purchaseDate) }}
</template>

<script lang="ts">
import {
  defineComponent, onMounted, computed,
} from 'vue';
import gqlRequest from '../dashboard/gql-request';
import defaultProfileImage from '@/assets/images/default-profile.jpeg';

export default defineComponent({
  name: 'WebAppStickersGridTemplate',
  props: {
    stickers: { type: Array, default: () => [{}] },
    showPurchaseDetails: { type: Boolean, default: false },
    socialShopId: { type: String, default: null },
  },
  setup(props) {
    const {
      route, router, loading,
    } = gqlRequest();

    const isEmbedded = computed(() => route.path.includes('/embed/'));

    function gotoStickerDetails(stickerId: any) {
      // Preserve query parameters and extract organization ID from embed context
      const queryParams = new URLSearchParams();

      // Copy existing query parameters
      if (route.query) {
        Object.entries(route.query).forEach(([key, value]) => {
          if (value) queryParams.set(key, String(value));
        });
      }

      // Extract organization ID from embedded organization path
      if (isEmbedded.value && route.path.includes('/embed/organization/')) {
        const orgIdMatch = route.path.match(/\/embed\/organization\/([^/?]+)/);
        if (orgIdMatch) {
          queryParams.set('embedOrganizationId', orgIdMatch[1]);
        }
      }

      // Add socialShopId if provided
      if (props.socialShopId) {
        queryParams.set('socialShopId', props.socialShopId);
      }

      const queryString = queryParams.toString();
      const queryParam = queryString ? `?${queryString}` : '';

      if (isEmbedded.value) {
        router.push(`/web/embed/sticker/${stickerId}${queryParam}`);
      } else {
        router.push(`/web/sticker/${stickerId}${queryParam}`);
      }
    }

    function formatDate(dateString: string) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }

    const getPlaceholderForEntity = (item: any): string => {
      // Check if entity is an organization
      if (item.entity === 'Organization' && item.organization) {
        return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23e8f5f4" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" font-size="40" fill="%231fbeaf"%3E🏢%3C/text%3E%3C/svg%3E';
      }
      // Return default profile image for users
      return defaultProfileImage;
    };

    const handleImageError = (event: Event, item?: any) => {
      const target = event.target as HTMLImageElement;
      if (!target) return;

      // Prevent infinite loop if default image also fails
      if (target.src === defaultProfileImage || target.src.startsWith('data:image/svg')) return;

      // Use appropriate placeholder based on entity type
      target.src = item ? getPlaceholderForEntity(item) : defaultProfileImage;
    };

    const getCampaignBadgeClass = (campaignType: string): string => {
      if (campaignType === 'Sweepstakes') {
        return 'badge-sweepstakes';
      }
      return 'badge-default';
    };

    onMounted(async () => {
      loading.value = true;
      loading.value = false;
    });

    return {
      route,
      router,
      loading,
      isEmbedded,
      defaultProfileImage,
      // data
      gotoStickerDetails,
      formatDate,
      getPlaceholderForEntity,
      handleImageError,
      getCampaignBadgeClass,
    };
  },
});
</script>
<style lang="scss" scoped>
.stickers-list-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 640px) {
    gap: 20px;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 24px;
  }
}

.sticker-card {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px;
  overflow: hidden;

  &:hover {
    transform: translateY(-8px);

    .sticker-title h6 {
      color: #0d9488;
    }
  }
}

.sticker-image-wrapper {
  margin-bottom: 12px;
}

.list-image-container {
  position: relative;
  cursor: pointer;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  aspect-ratio: 3/4;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  background: #f9fafb;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 768px) {
    min-height: 240px;
    padding: 20px;
  }
}

.list-image-item {
  max-height: 100%;
  max-width: 100%;
  display: block;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  .sticker-card:hover & {
    transform: scale(1.05);
  }
}

// Campaign Badge
.campaign-badge {
  position: absolute;
  bottom: 12px;
  left: 0;
  padding: 6px 12px 6px 14px;
  border-radius: 0 8px 8px 0;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  span {
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &.badge-sweepstakes {
    background: linear-gradient(135deg, rgba(251, 191, 36, 0.95) 0%, rgba(245, 158, 11, 0.95) 100%);
    color: #000000;
  }

  &.badge-default {
    background: linear-gradient(135deg, rgba(13, 148, 136, 0.95) 0%, rgba(20, 184, 166, 0.95) 100%);
    color: #ffffff;
  }

  @media (min-width: 768px) {
    span {
      font-size: 12px;
    }
  }
}

// Sticker Info Section
.sticker-info-section {
  padding: 0 4px;
}

.creator-info {
  margin-bottom: 8px;

  .row {
    margin: 0;
  }

  .col-auto {
    padding: 0 8px 0 0;
  }

  .col {
    padding: 0;
  }

  img {
    border: 2px solid #e5e7eb;
    transition: border-color 0.2s;

    .sticker-card:hover & {
      border-color: #0d9488;
    }
  }
}

.sticker-author {
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  display: block;

  @media (min-width: 768px) {
    font-size: 14px;
  }
}

.sticker-title {
  margin-bottom: 6px;

  h6 {
    font-size: 15px;
    font-weight: 700;
    color: #1a1a1a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 0;
    line-height: 1.3;
    transition: color 0.2s;

    @media (min-width: 768px) {
      font-size: 16px;
    }
  }
}

// Sticker Pricing
.sticker-pricing {
  padding-left: 2px;

  .price-text {
    color: #0d9488;
    font-size: 14px;
    font-weight: 700;

    @media (min-width: 768px) {
      font-size: 15px;
    }
  }
}
</style>
